import { PurchaseRequest } from '../types'
import { SchoolLogo } from './SchoolLogo'
import './PurchaseRequestPrintView.css'

interface PurchaseRequestPrintViewProps {
  request: PurchaseRequest
  onBack: () => void
}

function PurchaseRequestPrintView({ request, onBack }: PurchaseRequestPrintViewProps) {
  const formatDate = (dateString?: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handlePrint = () => {
    // Изменяем название страницы для печати
    const originalTitle = document.title
    const requestNumber = request.id.substring(0, 8)
    document.title = `Žádanka o jednorázový nákup materiálu/služeb ${requestNumber}`
    
    // Печать
    window.print()
    
    // Восстанавливаем оригинальное название после печати
    setTimeout(() => {
      document.title = originalTitle
    }, 1000)
  }

  return (
    <>
      <div className="print-actions">
        <button onClick={onBack} className="btn-back">Zpět</button>
        <button onClick={handlePrint} className="btn-print">Tisk</button>
      </div>

      <div className="print-view">
        <div className="print-document">
        {/* Header with logo and organization info */}
        <div className="print-header">
          <div className="print-logo-section">
            <div className="print-logo">
              <SchoolLogo height="60px" width="auto" className="print-logo-img" />
            </div>
            <div className="organization-info">
              <p className="org-name">Integrovaná střední škola Cheb, příspěvková organizace</p>
              <p className="org-address">Obrněné brigády 2258/6, 350 02 Cheb</p>
              <p className="org-ids">IČ: 00077461; DIČ: CZ00077461; IZO: 130002046</p>
            </div>
          </div>
        </div>

        {/* Document title */}
        <h1 className="document-title">Žádanka o jednorázový nákup materiálu/služeb</h1>

        {/* Request number */}
        <div className="request-number">
          <strong>Číslo žádanky:</strong> {request.id.substring(0, 8)}
        </div>

        {/* Request details */}
        <div className="request-details-section">
          <div className="detail-row">
            <div className="detail-label">Název střediska ISŠ Cheb:</div>
            <div className="detail-value">{request.center_name || '-'}</div>
          </div>

          <div className="detail-row">
            <div className="detail-label">Odpovědná osoba:</div>
            <div className="detail-value">{request.created_by_name || '-'}</div>
          </div>

          <div className="detail-row">
            <div className="detail-label">E-Mail:</div>
            <div className="detail-value">{request.created_by_email || '-'}</div>
          </div>

          <div className="detail-row">
            <div className="detail-label">Předmět žádosti:</div>
            <div className="detail-value">{request.subject || '-'}</div>
          </div>

          <div className="detail-row">
            <div className="detail-label">ČÁSTKA v Kč vč. DPH:</div>
            <div className="detail-value">{request.amount ? `${request.amount.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč` : '-'}</div>
          </div>

          <div className="detail-row full-width">
            <div className="detail-label">Důvod objednání:</div>
            <div className="detail-value">{request.reason || '-'}</div>
          </div>

          <div className="detail-row full-width">
            <div className="detail-label">Popis:</div>
            <div className="detail-value">{request.description || '-'}</div>
          </div>
        </div>

        {/* Supplier section */}
        {request.supplier && (
          <div className="supplier-section">
            <div className="section-title">Dodavatel (předpoklad):</div>
            <div className="supplier-details">
              <p>{request.supplier}</p>
              {(request as any).supplier_address && <p>{(request as any).supplier_address}</p>}
              {(request as any).supplier_phone && <p>{(request as any).supplier_phone}</p>}
              {(request as any).supplier_email && <p>{(request as any).supplier_email}</p>}
            </div>
          </div>
        )}

        {/* Approval and dates section */}
        <div className="approval-section">
          <div className="approval-row">
            <div className="approval-label">Status:</div>
            <div className="approval-value">
              {request.status === 'approved_by_director' ? 'Schváleno Ředitel' : 
               request.status === 'approved_by_economist' ? 'Schváleno Ekonom' :
               request.status === 'accepted_by_referent' ? 'Přijato Obchodním odd.' :
               request.status === 'ordered_by_referent' ? 'Objednáno' :
               request.status === 'completed' ? 'Dokončeno' : request.status}
            </div>
          </div>

          <div className="approval-row">
            <div className="approval-label">Vytvořeno:</div>
            <div className="approval-value">{formatDateTime(request.created_at)}</div>
          </div>

          {request.approved_by_economist_at && (
            <div className="approval-row">
              <div className="approval-label">Schváleno správcem rozpočtu:</div>
              <div className="approval-value">{formatDate(request.approved_by_economist_at)}</div>
            </div>
          )}

          {request.approved_by_director_at && (
            <div className="approval-row">
              <div className="approval-label">Datum schválení ředitelem:</div>
              <div className="approval-value">{formatDate(request.approved_by_director_at)}</div>
            </div>
          )}
        </div>

        {/* Order processing section */}
        <div className="order-processing-section">
          <h2 className="section-title">ZPRACOVÁNÍ OBJEDNÁVKY</h2>
          
          <div className="processing-row">
            <div className="processing-label">Přijato Obchodním odd.:</div>
            <div className="processing-value">
              {request.accepted_by_referent_at ? formatDate(request.accepted_by_referent_at) : ''}
            </div>
          </div>

          <div className="processing-row">
            <div className="processing-label">Objednáno:</div>
            <div className="processing-value">
              {request.ordered_by_referent_at ? formatDate(request.ordered_by_referent_at) : ''}
            </div>
          </div>

          <div className="processing-row">
            <div className="processing-label">Dokončeno:</div>
            <div className="processing-value">
              {request.completed_at ? formatDate(request.completed_at) : ''}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export default PurchaseRequestPrintView

